import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Link as MuiLink,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material"
import { CheckCircle, Download, Edit, FileUp, Trash2, XCircle } from "lucide-react"
import { useCallback, useEffect, useRef, useState, type JSX, type ReactNode } from "react"
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom"

import { BackButton } from "@/components/ui/back-button"
import { ConfirmDialog } from "@/components/ui/confirm-dialog"
import { PageLoader } from "@/components/ui/page-loader"
import { useToast } from "@/components/ui/use-toast"
import { PdfReader } from "@/components/pdf/pdf-reader"
import { Money } from "@crm-src/components/money"
import { ResellerOnly } from "@crm-src/components/reseller"
import { PurchaseOrderSourceChip, PurchaseOrderStatusChip } from "@crm-src/components/purchase-orders/purchase-order-chips"
import { PurchaseOrderDialog } from "@crm-src/components/purchase-orders/purchase-order-dialog"
import {
  centsToDollars,
  formatBytes,
  formatPurchaseOrderDate,
  partyLines,
  purchaseOrderCancelledStamp,
  purchaseOrderFileError,
  PURCHASE_ORDER_ACCEPT,
} from "@crm-src/components/purchase-orders/purchase-order-format"
import { purchaseOrdersApi, type PurchaseOrder } from "@crm-src/lib/purchase-orders-api"

type PreviewKind = "pdf" | "document"

/** Which previews this purchase order can show, in tab order. */
export function previewKinds(po: Pick<PurchaseOrder, "has_pdf" | "has_document">): PreviewKind[] {
  return [...(po.has_pdf ? (["pdf"] as const) : []), ...(po.has_document ? (["document"] as const) : [])]
}

function isPdf(po: PurchaseOrder): boolean {
  const type = po.document?.content_type ?? ""
  return type === "application/pdf" || /\.pdf$/i.test(po.document?.filename ?? "")
}

function isImage(po: PurchaseOrder): boolean {
  return (po.document?.content_type ?? "").startsWith("image/")
}

function Field({ label, children }: { label: string; children: ReactNode }): JSX.Element {
  return (
    <Box>
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.4 }}>
        {label}
      </Typography>
      <Box sx={{ fontSize: 14, mt: 0.5 }}>{children}</Box>
    </Box>
  )
}

const DASH = <Box component="span" sx={{ color: "text.disabled" }}>—</Box>

function Lines({ lines }: { lines: string[] }): JSX.Element {
  if (lines.length === 0) return DASH
  return (
    <>
      {lines.map((line, index) => (
        <Typography key={index} sx={{ fontSize: 14, fontWeight: index === 0 ? 500 : 400 }}>
          {line}
        </Typography>
      ))}
    </>
  )
}

function MoneyCell({ cents }: { cents: number | null | undefined }): JSX.Element {
  return cents === null || cents === undefined ? DASH : <Money value={centsToDollars(cents)} />
}

/**
 * One purchase order (`/crm/purchase-orders/:id`).
 *
 * Every write control follows the SERVER's `editable` verdict for this viewer
 * — never a client re-derivation. Money renders through `<Money>`; the reseller
 * party and the linked quote through `<ResellerOnly>`.
 *
 * Preview: the standard PO PDF crm-api renders from the lines (`has_pdf`) and
 * the buyer's own file (`has_document`), as two tabs when both exist. A PDF
 * goes through the shared `<PdfReader>` (pdf.js → canvas, never a blob
 * iframe), an image through an `<img>`, anything else is a download prompt.
 * Bytes are fetched for the ACTIVE tab only and re-fetched on every switch,
 * because pdf.js detaches the buffer it renders.
 *
 * Delete is crm-api's soft delete: the purchase order leaves the lists and the
 * record and its files are kept. Deleting is STAFF's alone — a reseller can
 * only cancel, from its own portal — and a cancelled order (by either side)
 * carries crm-api's stamp, shown here as "Cancelled on <date> by <who>".
 */
export default function PurchaseOrderDetailPage(): JSX.Element {
  const { id } = useParams()
  const navigate = useNavigate()
  const { toast } = useToast()

  const [po, setPo] = useState<PurchaseOrder | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [preview, setPreview] = useState<PreviewKind | null>(null)
  const [previewBytes, setPreviewBytes] = useState<ArrayBuffer | null>(null)
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [previewVersion, setPreviewVersion] = useState(0)

  const [editOpen, setEditOpen] = useState(false)
  const [confirmCancel, setConfirmCancel] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [busy, setBusy] = useState(false)
  const fileInput = useRef<HTMLInputElement | null>(null)

  const load = useCallback(async () => {
    if (!id) return
    setLoading(true)
    try {
      const record = await purchaseOrdersApi.get(id)
      setPo(record)
      setLoadError(null)
    } catch (err: any) {
      setPo(null)
      setLoadError(
        err?.response?.status === 404 ? "This purchase order does not exist or was deleted." : err?.response?.data?.error || "The purchase order could not be loaded.",
      )
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    void load()
  }, [load])

  // Keep the chosen preview valid for the record (a document attached later
  // adds a tab; the first load picks the generated PDF when there is one).
  useEffect(() => {
    if (!po) return
    const kinds = previewKinds(po)
    setPreview((current) => (current && kinds.includes(current) ? current : kinds[0] ?? null))
  }, [po])

  // Fetch the ACTIVE preview's bytes.
  useEffect(() => {
    if (!po || !preview) return
    if (preview === "document" && !isPdf(po) && !isImage(po)) return
    let cancelled = false
    let objectUrl: string | null = null
    setPreviewBytes(null)
    setImageUrl(null)
    setPreviewError(null)
    const read = preview === "pdf" ? purchaseOrdersApi.pdfBytes(po.id) : purchaseOrdersApi.documentBytes(po.id)
    read
      .then((bytes) => {
        if (cancelled) return
        if (preview === "document" && isImage(po)) {
          objectUrl = URL.createObjectURL(new Blob([bytes], { type: po.document?.content_type }))
          setImageUrl(objectUrl)
        } else {
          setPreviewBytes(bytes)
        }
      })
      .catch(() => {
        if (!cancelled) setPreviewError("The preview could not be loaded. Use Download to open the file.")
      })
    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [po?.id, po?.has_pdf, po?.has_document, po?.document?.filename, preview, previewVersion])

  const report = (err: any, fallback: string) => {
    const data = err?.response?.data
    toast({
      variant: "destructive",
      title: fallback,
      description: data?.details?.issues?.join(" ") || data?.error || "Please try again.",
    })
  }

  const setStatus = async (status: "accepted" | "cancelled") => {
    if (!po) return
    const updated = await purchaseOrdersApi.update(po.id, { status })
    setPo(updated)
    toast({ title: status === "accepted" ? `${po.number} marked accepted` : `${po.number} cancelled` })
  }

  const download = async (kind: PreviewKind) => {
    if (!po) return
    try {
      if (kind === "pdf") await purchaseOrdersApi.downloadPdf(po)
      else await purchaseOrdersApi.downloadDocument(po)
    } catch (err) {
      report(err, "Download failed")
    }
  }

  const replaceDocument = async (file: File | null) => {
    if (!po || !file) return
    const problem = purchaseOrderFileError(file)
    if (problem) {
      toast({ variant: "destructive", title: "File not accepted", description: problem })
      return
    }
    setBusy(true)
    try {
      const updated = await purchaseOrdersApi.uploadDocument(po.id, file)
      setPo(updated)
      setPreview("document")
      setPreviewVersion((v) => v + 1)
      toast({ title: po.has_document ? "Document replaced" : "Document attached", description: file.name })
    } catch (err) {
      report(err, "The document could not be uploaded")
    } finally {
      setBusy(false)
      if (fileInput.current) fileInput.current.value = ""
    }
  }

  if (loading && !po) return <PageLoader />

  if (!po) {
    return (
      <Box sx={{ display: "grid", gap: 2 }}>
        <BackButton to="/crm/purchase-orders" label="Back to purchase orders" />
        <Alert severity="error" data-cy="po-load-error">
          {loadError ?? "The purchase order could not be loaded."}
        </Alert>
      </Box>
    )
  }

  const kinds = previewKinds(po)
  const canAccept = po.editable && po.status === "received"
  // The SERVER's verdict, like `editable`. `cancellable` is only absent while a
  // crm-api without the field is deployed; then, and only then, the old staff
  // rule stands in.
  const canCancel = po.editable && (po.cancellable ?? po.status !== "cancelled")
  const cancelledStamp = purchaseOrderCancelledStamp(po)
  const subtotalKnown = po.subtotal_cents !== null && po.subtotal_cents !== undefined

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }} data-cy="po-detail">
      <BackButton to="/crm/purchase-orders" label="Back to purchase orders" sx={{ alignSelf: "flex-start" }} />

      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
        <Box sx={{ display: "grid", gap: 1 }}>
          <Typography component="h1" sx={{ fontSize: 24, fontWeight: 700 }} data-cy="po-title">
            Purchase order {po.number}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
            <PurchaseOrderStatusChip status={po.status} />
            <PurchaseOrderSourceChip source={po.source} />
            {po.po_number && (
              <Typography sx={{ fontSize: 14, color: "text.secondary" }} data-cy="po-buyer-number">
                Buyer PO {po.po_number}
              </Typography>
            )}
          </Box>
          {cancelledStamp && (
            <Typography sx={{ fontSize: 13, color: "text.secondary" }} data-cy="po-cancelled-stamp">
              {cancelledStamp}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }} data-cy="po-actions">
          {po.has_pdf && (
            <Button variant="outlined" startIcon={<Download size={16} />} onClick={() => download("pdf")} data-cy="po-download-pdf">
              Download PDF
            </Button>
          )}
          {po.has_document && (
            <Button variant="outlined" startIcon={<Download size={16} />} onClick={() => download("document")} data-cy="po-download-document">
              Download document
            </Button>
          )}
          {po.editable && (
            <>
              {canAccept && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircle size={16} />}
                  disabled={busy}
                  data-cy="po-mark-accepted"
                  onClick={async () => {
                    setBusy(true)
                    try {
                      await setStatus("accepted")
                    } catch (err) {
                      report(err, "The status could not be changed")
                    } finally {
                      setBusy(false)
                    }
                  }}
                >
                  Mark accepted
                </Button>
              )}
              <Button variant="outlined" startIcon={<Edit size={16} />} onClick={() => setEditOpen(true)} data-cy="po-edit">
                Edit
              </Button>
              <Button
                variant="outlined"
                startIcon={<FileUp size={16} />}
                disabled={busy}
                onClick={() => fileInput.current?.click()}
                data-cy="po-replace-document"
              >
                {po.has_document ? "Replace document" : "Attach document"}
              </Button>
              <input
                ref={fileInput}
                type="file"
                hidden
                accept={PURCHASE_ORDER_ACCEPT}
                data-cy="po-replace-document-input"
                onChange={(e) => void replaceDocument(e.target.files?.[0] ?? null)}
              />
              {canCancel && (
                <Button color="inherit" startIcon={<XCircle size={16} />} onClick={() => setConfirmCancel(true)} data-cy="po-cancel">
                  Cancel order
                </Button>
              )}
              <Button color="error" startIcon={<Trash2 size={16} />} onClick={() => setConfirmDelete(true)} data-cy="po-delete">
                Delete
              </Button>
            </>
          )}
        </Box>
      </Box>

      <Card>
        <CardContent sx={{ display: "grid", gap: 3, gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" } }}>
          <Field label="Buyer">
            <Lines lines={partyLines(po.buyer)} />
          </Field>
          <Field label="Ship to">
            <Lines lines={partyLines(po.ship_to)} />
          </Field>
          <Field label="Customer">
            {po.customer ? (
              <MuiLink component={RouterLink} to={`/crm/customers/${po.customer.id}`} data-cy="po-customer-link">
                {po.customer.company_name}
              </MuiLink>
            ) : (
              DASH
            )}
          </Field>
          <ResellerOnly>
            <Field label="Reseller">
              {po.reseller ? (
                <MuiLink component={RouterLink} to={`/crm/administration/resellers/${po.reseller.id}`} data-cy="po-reseller-link">
                  {po.reseller.name}
                  {po.reseller.code ? ` · ${po.reseller.code}` : ""}
                </MuiLink>
              ) : (
                DASH
              )}
              {po.reseller_quote && (
                <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.5 }} data-cy="po-quote">
                  From quote {po.reseller_quote.number}
                </Typography>
              )}
            </Field>
          </ResellerOnly>
          <Field label="Order date">{formatPurchaseOrderDate(po.order_date)}</Field>
          <Field label="Submitted by">{po.submitted_by_email || DASH}</Field>
          <Field label="Recorded by">{po.created_by?.full_name || (po.source === "upload" ? DASH : "The reseller")}</Field>
          <Field label="Recorded">{formatPurchaseOrderDate(po.created_at)}</Field>
        </CardContent>
      </Card>

      <Card data-cy="po-lines">
        <CardContent sx={{ display: "grid", gap: 1.5 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 600 }}>Lines</Typography>
          {po.lines.length === 0 ? (
            <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
              No lines — this purchase order is the attached document.
            </Typography>
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Part number</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell align="right">Unit price</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {po.lines.map((line, index) => (
                    <TableRow key={index} data-cy="po-line">
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{line.part_number || DASH}</TableCell>
                      <TableCell>{line.description || DASH}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                        {line.quantity}
                        {line.unit ? ` ${line.unit}${line.quantity === 1 ? "" : "s"}` : ""}
                      </TableCell>
                      <TableCell align="right">
                        <MoneyCell cents={line.unit_price_cents} />
                      </TableCell>
                      <TableCell align="right">
                        <MoneyCell cents={line.line_total_cents} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
          <Box sx={{ display: "grid", justifyContent: "end", gap: 0.5, pr: 2 }}>
            {subtotalKnown && (
              <Typography sx={{ fontSize: 14, textAlign: "right" }}>
                Subtotal <MoneyCell cents={po.subtotal_cents} />
              </Typography>
            )}
            <Typography sx={{ fontSize: 16, fontWeight: 700, textAlign: "right" }} data-cy="po-total">
              Total <MoneyCell cents={po.total_cents} />
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {po.note && (
        <Card>
          <CardContent>
            <Field label="Note">
              <Typography sx={{ fontSize: 14, whiteSpace: "pre-wrap" }} data-cy="po-note">
                {po.note}
              </Typography>
            </Field>
          </CardContent>
        </Card>
      )}

      <Card data-cy="po-preview">
        <CardContent sx={{ display: "grid", gap: 2 }}>
          {kinds.length > 1 ? (
            <Tabs value={preview ?? kinds[0]} onChange={(_e, next: PreviewKind) => setPreview(next)} aria-label="Purchase order preview">
              <Tab value="pdf" label="Generated PDF" data-cy="po-preview-tab-pdf" />
              <Tab value="document" label="Buyer's document" data-cy="po-preview-tab-document" />
            </Tabs>
          ) : (
            <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
              {kinds[0] === "pdf" ? "Generated PDF" : kinds[0] === "document" ? "Buyer's document" : "Preview"}
            </Typography>
          )}

          {kinds.length === 0 && (
            <Typography sx={{ fontSize: 14, color: "text.secondary" }}>Nothing to preview — no lines and no document.</Typography>
          )}

          {previewError && <Alert severity="warning">{previewError}</Alert>}

          {preview === "pdf" && !previewError && (
            <PdfReader bytes={previewBytes} filename={`${po.number}.pdf`} containerTestId="po-pdf-preview" fallbackTestId="po-pdf-fallback" />
          )}

          {preview === "document" && !previewError && po.document && (
            <>
              {isPdf(po) && (
                <PdfReader
                  bytes={previewBytes}
                  filename={po.document.filename}
                  containerTestId="po-document-preview"
                  fallbackTestId="po-document-fallback"
                />
              )}
              {isImage(po) && imageUrl && (
                <Box
                  component="img"
                  src={imageUrl}
                  alt={po.document.filename}
                  data-cy="po-document-image"
                  sx={{ maxWidth: "100%", borderRadius: 1, border: 1, borderColor: "divider" }}
                />
              )}
              {!isPdf(po) && !isImage(po) && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }} data-cy="po-document-download-prompt">
                  <Typography sx={{ fontSize: 14 }}>
                    {po.document.filename}{" "}
                    <Box component="span" sx={{ color: "text.secondary" }}>
                      {formatBytes(po.document.byte_size)}
                    </Box>{" "}
                    can&apos;t be previewed here.
                  </Typography>
                  <Button size="small" variant="outlined" startIcon={<Download size={14} />} onClick={() => download("document")}>
                    Download to open it
                  </Button>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {po.editable && (
        <PurchaseOrderDialog
          open={editOpen}
          purchaseOrder={po}
          onClose={() => setEditOpen(false)}
          onSaved={(updated) => {
            setEditOpen(false)
            setPo(updated)
            setPreviewVersion((v) => v + 1)
            toast({ title: "Purchase order saved" })
          }}
        />
      )}

      <ConfirmDialog
        isOpen={confirmCancel}
        onClose={() => setConfirmCancel(false)}
        title={`Cancel ${po.number}?`}
        description="The purchase order is marked cancelled. It stays on the list and can be edited, but it no longer counts as an order XOP is working on."
        variant="warning"
        confirmLabel="Cancel order"
        cancelLabel="Keep it"
        onConfirm={async () => {
          try {
            await setStatus("cancelled")
          } catch (err) {
            report(err, "The status could not be changed")
            throw err
          }
        }}
      />

      <ConfirmDialog
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title={`Delete ${po.number}?`}
        description="It is removed from the purchase order lists and search. The record and its files are kept, and an administrator can restore it."
        confirmLabel="Delete"
        onConfirm={async () => {
          try {
            await purchaseOrdersApi.remove(po.id)
          } catch (err) {
            report(err, "The purchase order could not be deleted")
            throw err
          }
          toast({ title: `${po.number} deleted` })
          navigate("/crm/purchase-orders")
        }}
      />
    </Box>
  )
}
